import { LuIcon } from "@overmap-ai/blocks"
import type { GeoImage, Stored } from "@overmap-ai/common"
import { Video } from "lucide-react"
import { memo, useCallback, useMemo, useState } from "react"
import type { MarkerEvent, MarkerInstance } from "react-map-gl/mapbox"

import type { ModelAnnotatedEventHandler } from "../../typings"
import { Marker } from "../components"
import { useMarkerEvent } from "../hooks"

interface GeoImageMarkerProps {
	geoImage: Stored<GeoImage>
	onClick?: ModelAnnotatedEventHandler<MarkerEvent<MouseEvent>, Stored<GeoImage>>
	onContextMenu?: ModelAnnotatedEventHandler<MarkerEvent<MouseEvent>, Stored<GeoImage>>
}

export const GeoImageMarker = memo<GeoImageMarkerProps>((props) => {
	const { geoImage, onClick, onContextMenu } = props

	const [marker, setMarker] = useState<MarkerInstance | null>(null)

	const coordinates = useMemo(() => {
		return geoImage.geo_marker?.coordinates
	}, [geoImage.geo_marker?.coordinates])

	const directionAngle = useMemo(() => {
		// Convert direction (0-360 degrees) to CSS rotation angle
		// Direction represents the compass bearing the photo was taken towards
		// Subtract 90 degrees to make 0 degrees (north) face east when not rotated
		return geoImage.direction != null ? geoImage.direction - 90 : null
	}, [geoImage.direction])

	const handleClick = useCallback(
		(e: MarkerEvent<MouseEvent>) => {
			onClick?.({ event: e, model: geoImage })
		},
		[geoImage, onClick],
	)

	useMarkerEvent(marker ?? undefined, "click", handleClick)

	const handleContextMenu = useCallback(
		(e: MarkerEvent<MouseEvent>) => {
			onContextMenu?.({ event: e, model: geoImage })
		},
		[geoImage, onContextMenu],
	)

	useMarkerEvent(marker ?? undefined, "contextmenu", handleContextMenu)

	if (!coordinates) return null

	return (
		<Marker ref={setMarker} longitude={coordinates[0]} latitude={coordinates[1]} anchor="center">
			<div
				className="bg-(--base-3) ring-(--base-12) relative flex size-[20px] cursor-pointer items-center justify-center rounded-full p-1 ring"
				style={{
					transform: directionAngle != null ? `rotate(${directionAngle}deg)` : undefined,
				}}
			>
				<LuIcon icon={Video} className="pointer-events-none" size="14" />
			</div>
		</Marker>
	)
}, areGeoImageMarkersEqual)
GeoImageMarker.displayName = "GeoImageMarker"

function areGeoImageMarkersEqual(prevProps: GeoImageMarkerProps, nextProps: GeoImageMarkerProps) {
	// handlers
	if (prevProps.onClick !== nextProps.onClick) return false
	if (prevProps.onContextMenu !== nextProps.onContextMenu) return false

	const prevGeoImage = prevProps.geoImage
	const nextGeoImage = nextProps.geoImage

	// marker style
	if (prevGeoImage.direction !== nextGeoImage.direction) return false

	// location
	const prevGeoMakrer = prevGeoImage.geo_marker
	const nextGeoMakrer = nextGeoImage.geo_marker

	if (prevGeoMakrer?.coordinates[0] !== nextGeoMakrer?.coordinates[0]) return false
	if (prevGeoMakrer?.coordinates[1] !== nextGeoMakrer?.coordinates[1]) return false

	return true
}
